import { useRef, useMemo } from "react";
import Link from "next/link";
import logInternalLinkClick from "lib/ga/internalLinkClick";
import styles from './card.module.css'
import badgeStyle from 'components/_styled/badge.module.css';
import LazyImage from "components/LazyImage";
import interpretThumbnail from "lib/interpretThumbnail";
import { checkLoggedIn } from "context/context";
import { ResultThumbnail } from "lib/types";

const getThumbnail = (images: [{format: object}], name: string, searchType: string, categoryCode: string) => {
  const thumb: ResultThumbnail = {
    origin: '',
    default: searchType === "SCHEME" && categoryCode === "SC"
      ? "fallback-images/scheme-sc-fullback.jpeg"
      : "fallback-images/" + categoryCode?.toLowerCase() + "-fullback.jpeg",
    sizes: [200, 400, 600, 800],
  };

  // get the real one
  if (images && images.length) {
    thumb.origin = "this";
    Object.keys(images[0].format)
      .map((k) => ({ ...images[0].format[k], file_type: k.endsWith('wp') ? 'webp' : 'jpg' }))
      .forEach((med) => {
        if (thumb.sizes.includes(med.width)) {
          if (med.file_type in thumb) thumb[med.file_type][med.width] = med.uri;
          else
            thumb[med.file_type] = {
              [med.width]: med.uri,
            };
        }
      });

    if ("jpg" in thumb) {
      thumb.default = thumb.jpg?.[200];
    } else if ("png" in thumb) thumb.default = thumb.png?.[200];
  }
  return interpretThumbnail(thumb, name, '400');
};

type Props = {
  url: string, 
  type: string, 
  full_name: string | undefined, 
  category_code: string, 
  thumbnail: undefined, 
  sponsored: boolean | undefined, 
  description: string, 
  price: string, 
  availability: object, 
  size: string | undefined, 
  rent: string |undefined, 
  id: {
    raw: string
  }, 
  marketedBy: [
    {
      id: string,
      name: string
    }
  ], 
  owners: [
    {
      company_id: string,
      company_name: string
      paying_client: boolean,
      name: string,
      logo_uri: string
    }
  ], 
  modified: string, 
  images: [{format: object}], 
  uri: object, 
  name: string, 
  number_of_available_properties: number | undefined, 
  line1: string, 
  line2: object, 
  town_name: string, 
  line3: object, 
  postcode: string, 
  url_fragment: string, 
  themeFormat: {
    primary: string,
    secondary: string
  },
  use_class: any
}

const Card = ({
  url,
  type,
  full_name,
  category_code,
  thumbnail,
  sponsored,
  description,
  price,
  availability,
  size,
  use_class,
  rent,
  id,
  marketedBy,
  owners,
  modified,
  images,
  uri,
  name,
  number_of_available_properties,
  line1,
  line2,
  town_name,
  line3,
  postcode,
  url_fragment,
  themeFormat
}: Props) => {

  const resultLinkRef = useRef<HTMLAnchorElement>(null);
  const loggedIn = checkLoggedIn();

  const updated = modified ? new Date(modified) : null;
  const thumbnailProps = useMemo(() => {
    return getThumbnail(images, name, type, category_code);
  }, [thumbnail, name, type, category_code]);

  let resultHref = ''
  if (type === 'scheme') {
    resultHref = url || `/scheme/${uri}-${id.raw}`;
  } else {
    resultHref = `/property/${url_fragment}`;
  }
  var addressDisplay = [line1 === full_name ? null : line1, line2, line3, town_name].filter(Boolean).filter(value => Object.keys(value).length !== 0).join(", ");
  addressDisplay = [addressDisplay, postcode].filter(Boolean).join(" | ");

  return (
    <li className={styles.ListItem} id={`card-${id.raw}`} key={id.raw}>
      <div className={`
          ${styles.OuterContainer}
          ${sponsored ? styles.schemeStyle : styles.propertyBorder}
        `}
        style={sponsored ? {
          borderColor: themeFormat.primary,
          backgroundColor: themeFormat.primary
        } : {}}
      >
        <article className={styles.Container}
          onClick={(e) => {
            if (
              (e as any).target.tagName.toUpperCase() !== "BUTTON" &&
              (e as any).target.tagName.toUpperCase() !== "A"
            ) {
              resultLinkRef?.current?.click();
            }
          }}
          title={full_name}
        >
          <div className={`${styles.Thumb} ${type === 'scheme' ? styles.schemeImage : ''}`}>
            <LazyImage {...thumbnailProps} />
            {/* Dont't show if an unsponsored property */}
            {(sponsored) && (
              <div className={`${styles.badgeCard} ${badgeStyle.badge} ${badgeStyle.SponsoredBadge}`} style={{ background: themeFormat.primary, border: `1px solid ${themeFormat.primary}` }}>
                <span>Featured</span>
              </div>
            )}
          </div>
          <Link href={resultHref} passHref prefetch={false} legacyBehavior>
            <a
              ref={resultLinkRef}
              aria-label={`View ${full_name} on Completely Retail`}
              onClick={(e) => {
                logInternalLinkClick("Scheme Search Result", url, id.raw);
              }}

              className={styles.cardHeading}
            >
              <h2>{(typeof name === 'string') && name}</h2>
              {addressDisplay && (
                <p className={styles.Address}>
                  <em className={styles.AddressEm}>{addressDisplay}</em>
                </p>
              )}
            </a>
          </Link>
          {description && (
            <div className={styles.CardDescription}>{description}</div>
          )}
          <div className={styles.StatsTray}>
            {availability && (
              <div className={styles.TagTitle}>Availability: <span>{availability.join(' / ')}</span></div>
            )}
            {size && (<>
              <div className={styles.TagTitle}>Size: <span>{size ? parseInt(size).toLocaleString() : "N/A"} sq ft</span> </div>              </>
            )}
            {
              use_class && (
                <div className={styles.TagTitle}>Use Class: <span>{use_class}</span> </div>
              )}
            {availability && (
              <div className={styles.TagTitle}>Rent: <span>{rent ? `£${parseInt(rent).toLocaleString()} pa` : "Price on Application"}</span> </div>
            )}
            {price && price === "0" && (
              <div className={styles.TagTitle}>Price: <span> {`£${parseInt(price).toLocaleString()}`}</span></div>
            )}
            {!sponsored && modified && (
              <div className={`${styles.TagTitle} ${styles.noWrap}`}>Last modified: <span>{modified}</span></div>
            )}
          </div>
          {((Array.isArray(marketedBy) && marketedBy.length > 0) || (owners && owners.length > 0)) && (
            <div className={styles.RepsContainer} >
              {Array.isArray(marketedBy) && marketedBy.length > 0 && (
                <div className={styles.MarketedBy}>

                  {marketedBy.map((company) => {
                    if (loggedIn) {
                      return (
                        <Link key={company.id} href={`/overview/${company.id}`} passHref prefetch={false} legacyBehavior>
                          <a
                            className={styles.marketedByLink}
                            onClick={(e) => e.stopPropagation()}
                            title={"Explore " + company.name + " on Completely Retail"}
                          >
                            {company.name}
                          </a>
                        </Link>
                      )
                    } else {
                      return (<p key={company.id} className={styles.marketedBy}>{company.name}</p>)
                    }
                  })}
                </div>
              )}
              {loggedIn && owners && owners.length > 0 && (
                <div className={styles.OwnersContainer}>
                  {owners.map((owner) => {
                    if (owner.paying_client) {
                      return (
                        <Link key={`${owner.company_id}`} href={`/overview/${owner.company_id}`} passHref prefetch={false} onClick={(e) => e.stopPropagation()} title={"Explore " + owner.name + " on Completely Retail"}>
                          <img key={owner.company_id} alt={`${owner.company_name} logo`} src={`${process.env.NEXT_PUBLIC_APP_MEDIA_URL}${owner.logo_uri}`} loading="lazy" height="auto" width="auto"></img>
                        </Link>
                      )
                    }
                  }
                  )}
                </div>
              )}
            </div>
          )}
        </article>
        {Array.isArray(number_of_available_properties) && number_of_available_properties > 0 && (
          <div style={type === 'scheme' ? { backgroundColor: sponsored ? themeFormat.primary : '#E0E0E0' } : {}} className={type === 'scheme' ? `${styles.schemeTray} ${styles.Tray}` : styles.Tray}>
            <div style={{ backgroundColor: sponsored ? themeFormat.secondary : "rgb(45,53,58)" }} className={styles.Amount}>{number_of_available_properties} available units</div>
          </div>
        )}


      </div>
    </li>
  )
};

export default Card;
